import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Save, Eye, Code, Monitor, Smartphone, Tablet, ToggleLeft, ToggleRight, Palette, Image, Trash2, Plus, X } from 'lucide-react';
import { useSite } from '@/hooks/useFirestore';
import { cloudFunctions } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { GlassCard } from '@/components/ui/GlassCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { renderTemplate, type BusinessInfo, type CustomTheme, getTemplateTheme } from '@shared/siteRenderer';
import { FEATURES, FEATURE_CATEGORIES, getDefaultEnabledFeatures, type FeatureCategory } from '@/lib/features';
import { TEMPLATES } from '@/lib/templates';
import { DashboardBackground } from '@/components/three';

// Custom color picker component
function ColorPicker({ label, value, onChange, presets }: {
  label: string;
  value: string;
  onChange: (color: string) => void;
  presets?: string[];
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        <div className="relative">
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-12 h-12 rounded-lg cursor-pointer border-2 border-white/20 bg-transparent"
            style={{ padding: 0 }}
          />
        </div>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="w-28 font-mono text-sm"
        />
        {presets && (
          <div className="flex gap-1">
            {presets.map((preset) => (
              <button
                key={preset}
                onClick={() => onChange(preset)}
                className="w-6 h-6 rounded border border-white/20 hover:scale-110 transition-transform"
                style={{ backgroundColor: preset }}
                title={preset}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

type SiteContent = {
  headline: string;
  aboutText: string;
  services: Array<{ name: string; description: string; icon?: string; price?: string }>;
  testimonials: Array<{ name: string; role?: string; content: string; rating?: number }>;
  seo: { title: string; description: string };
  faq?: Array<{ question: string; answer: string }>;
  videoUrl?: string;
};

type BusinessData = {
  phone?: string | null;
  address?: string | null;
  rating?: number | null;
  reviewCount?: number;
  businessHours?: string[];
  photos?: string[];
  googleMapsUrl?: string | null;
  googleReviews?: Array<{ authorName: string; rating: number; text: string; relativeTime?: string }>;
};

type ViewMode = 'desktop' | 'tablet' | 'mobile';

const DEFAULT_THEME: Required<CustomTheme> = {
  primaryColor: '#667eea',
  accentColor: '#764ba2',
  bgColor: '#ffffff',
  textColor: '#1f2937',
  bodyTextColor: '#6b7280',
  headingFont: 'Inter',
  bodyFont: 'Inter',
};

const normalizeTheme = (theme: CustomTheme): Required<CustomTheme> => ({
  primaryColor: theme.primaryColor ?? DEFAULT_THEME.primaryColor,
  accentColor: theme.accentColor ?? DEFAULT_THEME.accentColor,
  bgColor: theme.bgColor ?? DEFAULT_THEME.bgColor,
  textColor: theme.textColor ?? DEFAULT_THEME.textColor,
  bodyTextColor: theme.bodyTextColor ?? DEFAULT_THEME.bodyTextColor,
  headingFont: theme.headingFont ?? DEFAULT_THEME.headingFont,
  bodyFont: theme.bodyFont ?? DEFAULT_THEME.bodyFont,
});

const normalizeBusinessData = (data: BusinessData): BusinessInfo => ({
  phone: data.phone ?? undefined,
  address: data.address ?? undefined,
  rating: data.rating ?? undefined,
  reviewCount: data.reviewCount,
  businessHours: data.businessHours,
  photos: data.photos,
  googleMapsUrl: data.googleMapsUrl ?? undefined,
});

export function SiteEditorPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { data: site, isLoading } = useSite(siteId);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('desktop');
  const [featureCategory, setFeatureCategory] = useState<FeatureCategory>('essential');
  const [content, setContent] = useState<SiteContent>({
    headline: '',
    aboutText: '',
    services: [],
    testimonials: [],
    seo: { title: '', description: '' },
    faq: [],
    videoUrl: '',
  });
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(getDefaultEnabledFeatures());
  const [businessData, setBusinessData] = useState<BusinessData>({});
  const [sitePrice, setSitePrice] = useState<number>(29900); // Default $299.00 in cents
  const [customTheme, setCustomTheme] = useState<CustomTheme>({
    primaryColor: '#667eea',
    accentColor: '#764ba2',
    bgColor: '#ffffff',
    textColor: '#1f2937',
  });
  const [newImageUrl, setNewImageUrl] = useState('');

  // Update business photos
  const addPhoto = () => {
    if (newImageUrl.trim()) {
      setBusinessData(prev => ({
        ...prev,
        photos: [...(prev.photos || []), newImageUrl.trim()]
      }));
      setNewImageUrl('');
    }
  };

  const removePhoto = (index: number) => {
    setBusinessData(prev => ({
      ...prev,
      photos: (prev.photos || []).filter((_, i) => i !== index)
    }));
  };

  const movePhoto = (index: number, direction: 'up' | 'down') => {
    const photos = [...(businessData.photos || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= photos.length) return;
    [photos[index], photos[newIndex]] = [photos[newIndex], photos[index]];
    setBusinessData(prev => ({ ...prev, photos }));
  };

  useEffect(() => {
    if (site?.content) {
      setContent({
        headline: site.content.headline || '',
        aboutText: site.content.aboutText || '',
        services: site.content.services || [],
        testimonials: site.content.testimonials || [],
        seo: site.content.seo || { title: '', description: '' },
        faq: site.content.faq || [],
        videoUrl: site.content.videoUrl || '',
      });
    }
    if (site?.selectedFeatures && site.selectedFeatures.length > 0) {
      setSelectedFeatures(site.selectedFeatures);
    }
    if (site?.businessData) {
      setBusinessData({
        phone: site.businessData.phone,
        address: site.businessData.address,
        rating: site.businessData.rating,
        reviewCount: site.businessData.reviewCount,
        businessHours: site.businessData.businessHours,
        photos: site.businessData.photos,
        googleMapsUrl: site.businessData.googleMapsUrl,
        googleReviews: site.businessData.googleReviews,
      });
    }
    if (site?.price) {
      setSitePrice(site.price);
    }
    const templateTheme = getTemplateTheme(site?.templateType || 'modern-gradient');
    const ct = site?.customTheme;
    setCustomTheme({
      primaryColor: ct?.primaryColor || templateTheme.primaryColor || '#667eea',
      accentColor: ct?.accentColor || templateTheme.accentColor || '#764ba2',
      bgColor: ct?.bgColor || templateTheme.bgColor || '#ffffff',
      textColor: ct?.textColor || templateTheme.textColor || '#1f2937',
      bodyTextColor: ct?.bodyTextColor || templateTheme.bodyTextColor || '#6b7280',
      headingFont: ct?.headingFont || templateTheme.headingFont || 'Inter',
      bodyFont: ct?.bodyFont || templateTheme.bodyFont || 'Inter',
    });
  }, [site]);

  const handleSave = async () => {
    if (!siteId) return;
    setSaving(true);
    try {
      const normalizedTheme = normalizeTheme(customTheme);
      await cloudFunctions.updateSiteContent({
        siteId,
        content,
        selectedFeatures,
        price: sitePrice,
        customTheme: normalizedTheme,
        businessData: {
          photos: businessData.photos,
        },
      });
      toast.success('Changes saved');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = (featureId: string) => {
    setSelectedFeatures(prev =>
      prev.includes(featureId)
        ? prev.filter(f => f !== featureId)
        : [...prev, featureId]
    );
  };

  const previewHtml = useMemo(() => {
    if (!site) return '';

    const normalizedTheme = normalizeTheme(customTheme);

    const fontFamilies = new Set([normalizedTheme.headingFont, normalizedTheme.bodyFont]);
    const fontsParam = Array.from(fontFamilies).map(f => `family=${encodeURIComponent(f)}:wght@300;400;500;600;700;800;900`).join('&');
    const googleFontsLink = `<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?${fontsParam}&display=swap" rel="stylesheet">`;
    const themeOverrides = `${googleFontsLink}<style>:root{--primary:${normalizedTheme.primaryColor}!important;--accent:${normalizedTheme.accentColor}!important;--bg:${normalizedTheme.bgColor}!important;--text:${normalizedTheme.textColor}!important;--text-muted:${normalizedTheme.bodyTextColor}!important;--text-light:${normalizedTheme.bodyTextColor}!important;--font-heading:'${normalizedTheme.headingFont}',sans-serif!important;--font-body:'${normalizedTheme.bodyFont}',sans-serif!important;}</style>`;
    const linkScript = `<script>document.addEventListener('click',function(e){var l=e.target.closest('a');if(!l)return;var h=l.getAttribute('href');if(!h)return;if(h.startsWith('#')){e.preventDefault();if(h==='#'){window.scrollTo({top:0,behavior:'smooth'});return;}var t=document.querySelector(h);if(t)t.scrollIntoView({behavior:'smooth'});return;}e.preventDefault();});<\/script>`;
    const injectBase = (html: string) => html.replace('<head>', `<head><base target="_self">${themeOverrides}`).replace('</body>', linkScript + '</body>');

    // Always use template renderer in editor so content edits reflect in preview
    const normalizedBusinessData = normalizeBusinessData(businessData);
    const html = renderTemplate(site.templateType || 'modern-gradient', {
      businessName: site.name,
      headline: content.headline,
      aboutText: content.aboutText,
      services: content.services,
      testimonials: content.testimonials,
      seo: content.seo,
      businessData: normalizedBusinessData,
      customTheme: normalizedTheme,
    }, selectedFeatures);
    return injectBase(html);
  }, [content, site, selectedFeatures, businessData, customTheme]);

  const previewWidth = viewMode === 'desktop' ? '100%' : viewMode === 'tablet' ? '768px' : '375px';
  const themeForUi = normalizeTheme(customTheme);

  if (isLoading) return <LoadingScreen />;

  if (!site) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-white">Site not found</h2>
        <Button className="mt-4" onClick={() => navigate('/dashboard/sites')}>
          Back to Sites
        </Button>
      </div>
    );
  }

  const featuresInCategory = FEATURES.filter(f => f.category === featureCategory);

  return (
    <DashboardBackground>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', padding: '16px', gap: '16px' }}>
        {/* Top header bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(20px)',
          borderRadius: '12px',
          padding: '12px 20px',
          border: '1px solid rgba(91, 58, 58, 0.15)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/sites')}>
              <X className="w-5 h-5" />
            </Button>
            <div style={{ height: '24px', width: '1px', background: 'rgba(255,255,255,0.1)' }} />
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center overflow-hidden">
                <img src="/wolfromulus.png" alt="Romulus" className="w-10 h-10 object-contain" />
              </div>
              <div>
                <h1 style={{ fontSize: '16px', fontWeight: 600, color: '#FFFBFE', margin: 0 }}>{site.name}</h1>
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>
                  {site.templateType}
                </p>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{
              background: 'rgba(91, 58, 58, 0.15)',
              color: '#B08080',
              padding: '6px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <Eye size={14} />
              Preview Only
            </span>
            <div style={{ display: 'flex', background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '4px' }}>
              {([
                { mode: 'desktop', icon: Monitor },
                { mode: 'tablet', icon: Tablet },
                { mode: 'mobile', icon: Smartphone },
              ] as const).map(({ mode, icon: Icon }) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  style={{
                    padding: '8px',
                    borderRadius: '6px',
                    border: 'none',
                    background: viewMode === mode ? 'rgba(91, 58, 58, 0.5)' : 'transparent',
                    color: viewMode === mode ? 'white' : 'rgba(255,255,255,0.6)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon size={18} />
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
              style={{ gap: '8px' }}
            >
              {showPreview ? <Code className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Button>
            <Button variant="outline" onClick={handleSave} loading={saving}>
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </div>

      <div style={{ display: 'flex', flex: 1, gap: '20px', overflow: 'hidden' }}>
        <div
          style={{
            width: showPreview ? '45%' : '100%',
            overflow: 'auto',
            transition: 'width 0.3s ease',
          }}
        >
          <Tabs defaultValue="content" className="w-full">
            <TabsList>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="design">Design</TabsTrigger>
              <TabsTrigger value="images">Images</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
              <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
              <TabsTrigger value="seo">SEO</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="mt-6">
              <GlassCard className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="headline">Headline</Label>
                  <Input
                    id="headline"
                    value={content.headline}
                    onChange={(e) => setContent({ ...content, headline: e.target.value })}
                    placeholder="Enter your main headline"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="aboutText">About Text</Label>
                  <Textarea
                    id="aboutText"
                    value={content.aboutText}
                    onChange={(e) => setContent({ ...content, aboutText: e.target.value })}
                    placeholder="Tell visitors about the business..."
                    className="min-h-[200px]"
                  />
                </div>

                {selectedFeatures.includes('video-section') && (
                  <div className="space-y-2">
                    <Label htmlFor="videoUrl">Video URL (YouTube/Vimeo)</Label>
                    <Input
                      id="videoUrl"
                      value={content.videoUrl || ''}
                      onChange={(e) => setContent({ ...content, videoUrl: e.target.value })}
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>
                )}
              </GlassCard>
            </TabsContent>

            <TabsContent value="design" className="mt-6">
              <GlassCard className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                    <Palette className="w-5 h-5 text-rose" />
                    Color Theme
                  </h3>
                  {(() => {
                    const tpl = TEMPLATES.find(t => t.id === site?.templateType);
                    if (tpl && !tpl.themeEditable) {
                      return (
                        <p className="text-sm text-amber-400/80 mb-6 p-3 rounded-lg bg-amber-400/10 border border-amber-400/20">
                          This template uses a signature color scheme that cannot be customized.
                        </p>
                      );
                    }
                    return (
                      <p className="text-sm text-gray-400 mb-6">
                        Customize the colors to match the business brand. Changes preview in real-time.
                      </p>
                    );
                  })()}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ColorPicker
                    label="Primary Color (Headers, Buttons)"
                    value={themeForUi.primaryColor}
                    onChange={(color) => setCustomTheme(prev => ({ ...prev, primaryColor: color }))}
                    presets={['#667eea', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6']}
                  />
                  <ColorPicker
                    label="Accent Color (Highlights, Icons)"
                    value={themeForUi.accentColor}
                    onChange={(color) => setCustomTheme(prev => ({ ...prev, accentColor: color }))}
                    presets={['#764ba2', '#3b82f6', '#ec4899', '#06b6d4', '#f97316']}
                  />
                  <ColorPicker
                    label="Background Color"
                    value={themeForUi.bgColor}
                    onChange={(color) => setCustomTheme(prev => ({ ...prev, bgColor: color }))}
                    presets={['#ffffff', '#f8fafc', '#1a1a2e', '#0d0d0d', '#f5f0e8']}
                  />
                  <ColorPicker
                    label="Text Color"
                    value={themeForUi.textColor}
                    onChange={(color) => setCustomTheme(prev => ({ ...prev, textColor: color }))}
                    presets={['#1f2937', '#ffffff', '#374151', '#f5f5f5', '#2c3e50']}
                  />
                </div>

                <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                  <h4 className="text-sm font-medium text-white mb-3">Preview Theme</h4>
                  <div
                    className="p-4 rounded-lg"
                    style={{ backgroundColor: themeForUi.bgColor }}
                  >
                    <h5
                      className="text-lg font-bold mb-2"
                      style={{ color: themeForUi.primaryColor }}
                    >
                      Sample Heading
                    </h5>
                    <p
                      className="text-sm mb-3"
                      style={{ color: themeForUi.textColor }}
                    >
                      This is how your body text will appear on the website.
                    </p>
                    <button
                      className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                      style={{ backgroundColor: themeForUi.accentColor }}
                    >
                      Sample Button
                    </button>
                  </div>
                </div>

                <div className="text-sm text-gray-500">
                  <p>💡 Tip: Ensure good contrast between text and background colors for readability.</p>
                </div>
              </GlassCard>
            </TabsContent>

            <TabsContent value="images" className="mt-6">
              <GlassCard className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                    <Image className="w-5 h-5 text-rose" />
                    Business Images
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">
                    Manage photos that appear on the website. The first image is used as the hero image.
                  </p>
                </div>

                {/* Add new image */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Paste image URL..."
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addPhoto()}
                  />
                  <Button onClick={addPhoto} disabled={!newImageUrl.trim()}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </div>

                {/* Image gallery */}
                <div className="space-y-3">
                  {(businessData.photos || []).length === 0 ? (
                    <div className="text-center py-8 border border-dashed border-white/20 rounded-lg">
                      <Image className="w-12 h-12 mx-auto text-gray-500 mb-3" />
                      <p className="text-gray-400">No images yet</p>
                      <p className="text-sm text-gray-500">Add image URLs above or they'll be pulled from Google Places</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {(businessData.photos || []).map((photo, index) => (
                        <div
                          key={index}
                          className="relative group aspect-video rounded-lg overflow-hidden border border-white/10"
                        >
                          <img
                            src={photo}
                            alt={`Business photo ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23333" width="100" height="100"/><text x="50%" y="50%" fill="%23666" text-anchor="middle" dominant-baseline="middle" font-size="12">Failed</text></svg>';
                            }}
                          />
                          {index === 0 && (
                            <span className="absolute top-2 left-2 px-2 py-1 bg-rose/90 text-white text-xs rounded font-medium">
                              Hero Image
                            </span>
                          )}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            {index > 0 && (
                              <button
                                onClick={() => movePhoto(index, 'up')}
                                className="p-2 bg-white/20 rounded hover:bg-white/30"
                                title="Move up (make hero)"
                              >
                                ↑
                              </button>
                            )}
                            {index < (businessData.photos?.length || 0) - 1 && (
                              <button
                                onClick={() => movePhoto(index, 'down')}
                                className="p-2 bg-white/20 rounded hover:bg-white/30"
                                title="Move down"
                              >
                                ↓
                              </button>
                            )}
                            <button
                              onClick={() => removePhoto(index)}
                              className="p-2 bg-red-500/50 rounded hover:bg-red-500/70"
                              title="Remove"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="text-sm text-gray-500">
                  <p>💡 Tip: Use high-quality images (at least 800px wide) for best results. The first image is featured prominently.</p>
                </div>
              </GlassCard>
            </TabsContent>

            <TabsContent value="services" className="mt-6">
              <GlassCard>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-white">Services</h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setContent({
                        ...content,
                        services: [...content.services, { name: '', description: '', icon: 'star', price: '' }],
                      })
                    }
                  >
                    Add Service
                  </Button>
                </div>

                <div className="space-y-4">
                  {content.services.map((service, index) => (
                    <div key={index} className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Service {index + 1}</Label>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setContent({
                              ...content,
                              services: content.services.filter((_, i) => i !== index),
                            })
                          }
                        >
                          Remove
                        </Button>
                      </div>
                      <Input
                        value={service.name}
                        onChange={(e) => {
                          const newServices = [...content.services];
                          newServices[index].name = e.target.value;
                          setContent({ ...content, services: newServices });
                        }}
                        placeholder="Service name"
                      />
                      <Textarea
                        value={service.description}
                        onChange={(e) => {
                          const newServices = [...content.services];
                          newServices[index].description = e.target.value;
                          setContent({ ...content, services: newServices });
                        }}
                        placeholder="Service description"
                        className="min-h-[80px]"
                      />
                      {selectedFeatures.includes('price-list') && (
                        <Input
                          value={service.price || ''}
                          onChange={(e) => {
                            const newServices = [...content.services];
                            newServices[index].price = e.target.value;
                            setContent({ ...content, services: newServices });
                          }}
                          placeholder="Price (e.g., $99, From $50)"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </GlassCard>
            </TabsContent>

            <TabsContent value="testimonials" className="mt-6">
              <GlassCard>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-white">Testimonials</h3>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setContent({
                        ...content,
                        testimonials: [
                          ...content.testimonials,
                          { name: '', role: '', content: '', rating: 5 },
                        ],
                      })
                    }
                  >
                    Add Testimonial
                  </Button>
                </div>

                <div className="space-y-4">
                  {content.testimonials.map((testimonial, index) => (
                    <div key={index} className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Testimonial {index + 1}</Label>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setContent({
                              ...content,
                              testimonials: content.testimonials.filter((_, i) => i !== index),
                            })
                          }
                        >
                          Remove
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          value={testimonial.name}
                          onChange={(e) => {
                            const newTestimonials = [...content.testimonials];
                            newTestimonials[index].name = e.target.value;
                            setContent({ ...content, testimonials: newTestimonials });
                          }}
                          placeholder="Customer name"
                        />
                        <Input
                          value={testimonial.role}
                          onChange={(e) => {
                            const newTestimonials = [...content.testimonials];
                            newTestimonials[index].role = e.target.value;
                            setContent({ ...content, testimonials: newTestimonials });
                          }}
                          placeholder="Role (e.g., Homeowner)"
                        />
                      </div>
                      <Textarea
                        value={testimonial.content}
                        onChange={(e) => {
                          const newTestimonials = [...content.testimonials];
                          newTestimonials[index].content = e.target.value;
                          setContent({ ...content, testimonials: newTestimonials });
                        }}
                        placeholder="Testimonial content"
                        className="min-h-[80px]"
                      />
                    </div>
                  ))}
                </div>
              </GlassCard>
            </TabsContent>

            <TabsContent value="features" className="mt-6">
              <GlassCard>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-2">Site Features</h3>
                  <p className="text-sm text-gray-400">Toggle features on/off to customize what appears on the site. Changes update the preview in real-time.</p>
                </div>

                <div className="flex gap-2 mb-6 flex-wrap">
                  {FEATURE_CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setFeatureCategory(cat.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        featureCategory === cat.id
                          ? 'bg-primary-500 text-white'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>

                <div className="space-y-3">
                  {featuresInCategory.map(feature => {
                    const isEnabled = selectedFeatures.includes(feature.id);
                    return (
                      <div
                        key={feature.id}
                        onClick={() => toggleFeature(feature.id)}
                        className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-colors ${
                          isEnabled ? 'bg-primary-500/20 border border-primary-500/50' : 'bg-white/5 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{feature.name}</span>
                          </div>
                          <p className="text-sm text-gray-400 mt-1">{feature.description}</p>
                        </div>
                        <div className="ml-4">
                          {isEnabled ? (
                            <ToggleRight className="w-8 h-8 text-primary-400" />
                          ) : (
                            <ToggleLeft className="w-8 h-8 text-gray-500" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="text-sm text-gray-400">
                    <span className="text-primary-400 font-medium">{selectedFeatures.length}</span> features enabled
                  </p>
                </div>
              </GlassCard>

              {selectedFeatures.includes('faq') && (
                <GlassCard className="mt-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-white">FAQ Questions</h3>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setContent({
                          ...content,
                          faq: [...(content.faq || []), { question: '', answer: '' }],
                        })
                      }
                    >
                      Add Question
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {(content.faq || []).map((item, index) => (
                      <div key={index} className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Question {index + 1}</Label>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setContent({
                                ...content,
                                faq: (content.faq || []).filter((_, i) => i !== index),
                              })
                            }
                          >
                            Remove
                          </Button>
                        </div>
                        <Input
                          value={item.question}
                          onChange={(e) => {
                            const newFaq = [...(content.faq || [])];
                            newFaq[index].question = e.target.value;
                            setContent({ ...content, faq: newFaq });
                          }}
                          placeholder="Question"
                        />
                        <Textarea
                          value={item.answer}
                          onChange={(e) => {
                            const newFaq = [...(content.faq || [])];
                            newFaq[index].answer = e.target.value;
                            setContent({ ...content, faq: newFaq });
                          }}
                          placeholder="Answer"
                          className="min-h-[80px]"
                        />
                      </div>
                    ))}
                  </div>
                </GlassCard>
              )}
            </TabsContent>

            <TabsContent value="seo" className="mt-6">
              <GlassCard className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="seoTitle">SEO Title</Label>
                  <Input
                    id="seoTitle"
                    value={content.seo.title}
                    onChange={(e) =>
                      setContent({ ...content, seo: { ...content.seo, title: e.target.value } })
                    }
                    placeholder="Page title for search engines"
                  />
                  <p className="text-xs text-gray-500">{content.seo.title.length}/60 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="seoDescription">Meta Description</Label>
                  <Textarea
                    id="seoDescription"
                    value={content.seo.description}
                    onChange={(e) =>
                      setContent({ ...content, seo: { ...content.seo, description: e.target.value } })
                    }
                    placeholder="Description for search engine results"
                    className="min-h-[100px]"
                  />
                  <p className="text-xs text-gray-500">{content.seo.description.length}/160 characters</p>
                </div>
              </GlassCard>
            </TabsContent>

            <TabsContent value="pricing" className="mt-6">
              <GlassCard className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">Set Your Price</h3>
                  <p className="text-sm text-gray-400 mb-4">
                    Choose how much to charge when a business purchases this website. You keep 100% of the sale.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="sitePrice">Sale Price (USD)</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                      <Input
                        id="sitePrice"
                        type="number"
                        min="49"
                        max="9999"
                        step="1"
                        value={sitePrice / 100}
                        onChange={(e) => {
                          const dollars = parseFloat(e.target.value) || 0;
                          setSitePrice(Math.round(dollars * 100));
                        }}
                        className="pl-8"
                        placeholder="299"
                      />
                    </div>
                    <p className="text-xs text-gray-500">Minimum $49, Maximum $9,999</p>
                  </div>

                  <div className="p-4 rounded-lg bg-primary-500/10 border border-primary-500/20">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Sale Price</span>
                      <span className="text-white font-medium">${(sitePrice / 100).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-400">Platform Fee (20%)</span>
                      <span className="text-gray-400">-${((sitePrice * 0.2) / 100).toFixed(2)}</span>
                    </div>
                    <div className="border-t border-primary-500/20 pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-primary-400 font-medium">Your Earnings</span>
                        <span className="text-primary-400 font-bold text-lg">${((sitePrice * 0.8) / 100).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-sm text-gray-500">
                    <p>💡 Tip: Research local web design prices in your target market. Most small business websites sell for $200-$500.</p>
                  </div>
                </div>
              </GlassCard>
            </TabsContent>
          </Tabs>
        </div>

        {showPreview && (
          <div
            style={{
              flex: 1,
              background: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '12px',
              border: '1px solid rgba(91, 58, 58, 0.3)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div
              style={{
                padding: '8px 16px',
                background: 'rgba(91, 58, 58, 0.1)',
                borderBottom: '1px solid rgba(91, 58, 58, 0.2)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <div style={{ display: 'flex', gap: '6px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(255, 95, 87, 0.8)' }} />
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(136, 136, 136, 0.8)' }} />
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(91, 58, 58, 0.8)' }} />
              </div>
              <span style={{ marginLeft: '12px', color: 'rgba(91, 58, 58, 0.6)', fontSize: '12px', fontFamily: 'monospace' }}>
                romulus@preview ~ {viewMode}
              </span>
            </div>
            <div
              style={{
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                justifyContent: 'center',
                padding: viewMode !== 'desktop' ? '20px' : '0',
                background: viewMode !== 'desktop' ? 'rgba(0, 0, 0, 0.3)' : 'transparent',
              }}
            >
              <iframe
                srcDoc={previewHtml}
                title="Site Preview"
                sandbox="allow-scripts allow-popups"
                style={{
                  width: previewWidth,
                  height: '100%',
                  border: 'none',
                  background: 'white',
                  borderRadius: viewMode !== 'desktop' ? '8px' : '0',
                  boxShadow: viewMode !== 'desktop' ? '0 10px 40px rgba(0,0,0,0.5)' : 'none',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        )}
      </div>
      </div>
    </DashboardBackground>
  );
}
